import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import SEO from "@/components/site/SEO";
import { articles, findArticle } from "@/data/articles";
import { SITE_URL, breadcrumbSchema } from "@/data/site";

export default function InsightsArticle() {
  const { slug } = useParams();
  const article = findArticle(slug);

  if (!article) {
    return (
      <div className="mx-auto max-w-2xl px-5 md:px-10 py-24 md:py-32 text-center">
        <SEO title="Article Not Found" description="This article could not be found." path={`/insights/${slug}`} noindex />
        <div className="eyebrow">404</div>
        <h1 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight">Article not found</h1>
        <Link to="/insights" className="mt-8 inline-flex items-center gap-2 text-[14px] font-bold link-underline">
          <ArrowLeft className="h-4 w-4" /> Back to Insights
        </Link>
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.summary,
    "articleBody": article.body.join("\n\n"),
    "author": { "@type": "Organization", "name": "LotWise Home" },
    "publisher": { "@type": "Organization", "name": "LotWise Home", "logo": { "@type": "ImageObject", "url": `${SITE_URL}/favicon-512.png` } },
    "mainEntityOfPage": `${SITE_URL}/insights/${article.slug}`,
  };

  const others = articles.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <div className="bg-white text-brand-black">
      <SEO
        title={article.title}
        description={article.summary}
        path={`/insights/${article.slug}`}
        type="article"
        schema={[
          articleSchema,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
            { name: article.title, path: `/insights/${article.slug}` },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="bg-brand-black text-white">
        <div className="mx-auto max-w-[900px] px-5 md:px-10 py-24 md:py-32">
          <Link to="/insights" className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-white/70 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Insights
          </Link>
          <div className="mt-8 flex items-center gap-3">
            <span className="text-[10px] tracking-[0.22em] uppercase text-brand-blue">{article.tag}</span>
            <span className="h-3 w-px bg-white/30" />
            <span className="text-[10px] tracking-[0.22em] uppercase text-white/50">{article.readingTime}</span>
          </div>
          <h1 className="mt-4 text-[34px] md:text-6xl font-bold tracking-[-0.02em] leading-[1.03]">
            {article.title}
          </h1>
          <p className="mt-8 text-[17px] md:text-[19px] leading-relaxed text-white/70">
            {article.summary}
          </p>
        </div>
      </section>

      {/* Body */}
      <article className="bg-white">
        <div className="mx-auto max-w-[720px] px-5 md:px-10 py-20 md:py-28 space-y-6">
          {article.body.map((p, i) => (
            <p key={i} className="text-[17px] leading-[1.75] text-brand-black/85">{p}</p>
          ))}
        </div>
      </article>

      {/* Related */}
      <section className="bg-[#F7F7F5]">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-20 md:py-28">
          <div className="eyebrow-accent">Keep Reading</div>
          <h2 className="mt-3 text-2xl md:text-4xl font-bold tracking-tight">More on the listing standard.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {others.map((a) => (
              <Link key={a.slug} to={`/insights/${a.slug}`} className="group bg-white border border-brand-gray/25 p-6 hover:border-brand-black transition-colors">
                <div className="text-[10px] tracking-[0.22em] uppercase text-brand-blue">{a.tag}</div>
                <h3 className="mt-3 text-[18px] font-bold leading-snug group-hover:text-brand-violet transition-colors">{a.title}</h3>
                <p className="mt-3 text-[13.5px] text-brand-gray line-clamp-3">{a.summary}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-black text-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-16 md:py-24 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <h3 className="text-xl md:text-3xl font-bold tracking-tight max-w-2xl">Move your next listing onto the standard.</h3>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-brand-black px-6 py-3.5 text-[13px] font-bold">
            Request Listing Support <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
